/**
 * GapsResults.js
 * Modern 2-column responsive layout with Gap Health Dashboard + Performance Radar.
 */
import React, { useState } from 'react';
import {
    Box, Grid, Paper, Typography, Chip, Avatar,
    LinearProgress, Alert, alpha, Tooltip,
    IconButton, Snackbar, useTheme, Button,
} from '@mui/material';
import {
    Analytics as AnalyticsIcon,
    TrendingUp as TrendingUpIcon,
    Tag as TagIcon,
    Search as SearchIcon,
    AutoAwesome as AutoAwesomeIcon,
    EditNote as EditNoteIcon,
    ContentCopy as CopyIcon,
    CheckCircleOutline as CopiedIcon,
    EmojiEvents as TrophyIcon,
    Warning as WarningIcon,
    Bolt as BoltIcon,
    Lock as LockIcon,
    ShowChart as RadarIcon,
    Bookmark as BookmarkIcon,
    BookmarkBorder as BookmarkBorderIcon,
    Lightbulb as TipIcon,
    RecordVoiceOver as VoiceIcon,
    VerifiedUser as NicheIcon,
    Tune as AlignIcon,
} from '@mui/icons-material';
import {
    RadarChart, Radar, PolarGrid, PolarAngleAxis,
    ResponsiveContainer, Legend, Tooltip as ChartTooltip,
} from 'recharts';
import { ScoreRing, SignalRow } from './GapsPrimitives';
import { PRIORITY_COLOR, PRIORITY_BG, SIGNAL_DEFINITIONS, alignmentColor, alignmentLabel } from './constants';
import { gapsApi } from '../../services/api';

// ── ErrorBoundary for sub-cards ──────────────────────────────────────────────
class CardErrorBoundary extends React.Component {
    constructor(props) { super(props); this.state = { hasError: false }; }
    static getDerivedStateFromError() { return { hasError: true }; }
    render() {
        if (this.state.hasError) {
            return (
                <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                    This section couldn't be rendered. Try refreshing.
                </Alert>
            );
        }
        return this.props.children;
    }
}

// ── Gap Health banner ─────────────────────────────────────────────────────────
function GapHealthBanner({ your, competitor, gaps }) {
    const theme_color = '#6366f1';
    const delta = competitor.overall_score - your.overall_score;
    const isAhead = delta <= 0;
    const absDelta = Math.abs(delta);
    const missingSignals = gaps.missing_signals?.length || 0;
    const missingKeywords = gaps.missing_keywords?.length || 0;
    const overlapPct = Math.round((gaps.topic_overlap || 0) * 100);

    const health = isAhead
        ? { label: 'Winning', color: '#10b981', bg: alpha('#10b981', 0.1), icon: <TrophyIcon sx={{ fontSize: 22 }} />, msg: 'Your content outperforms the competitor on overall score.' }
        : absDelta <= 10
        ? { label: 'On Track', color: '#f59e0b', bg: alpha('#f59e0b', 0.1), icon: <BoltIcon sx={{ fontSize: 22 }} />, msg: 'Small gaps — a few targeted tweaks will close the distance.' }
        : { label: 'Needs Work', color: '#ef4444', bg: alpha('#ef4444', 0.1), icon: <WarningIcon sx={{ fontSize: 22 }} />, msg: 'Significant gaps found. Follow the recommendations below to improve.' };

    return (
        <Paper elevation={0} sx={{
            p: 3, borderRadius: '14px',
            background: `linear-gradient(135deg, ${alpha(health.color, 0.07)}, ${alpha(theme_color, 0.05)})`,
            border: `1px solid ${alpha(health.color, 0.2)}`,
        }}>
            {/* Top row: health status */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Box sx={{
                    width: 48, height: 48, borderRadius: '12px', flexShrink: 0,
                    bgcolor: health.bg, color: health.color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {health.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>Gap Analysis Complete</Typography>
                        <Box sx={{
                            px: 1.5, py: 0.3, borderRadius: '7px',
                            bgcolor: health.bg, border: `1px solid ${alpha(health.color, 0.3)}`,
                        }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: health.color, fontSize: '0.72rem' }}>
                                {health.label}
                            </Typography>
                        </Box>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{health.msg}</Typography>
                </Box>
            </Box>

            {/* 4 quick-stat tiles */}
            <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }}>
                    <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: '10px', bgcolor: alpha(health.color, 0.06), border: `1px solid ${alpha(health.color, 0.15)}` }}>
                        <Typography sx={{ fontWeight: 900, fontSize: '1.6rem', color: health.color, lineHeight: 1 }}>
                            {isAhead ? `+${absDelta}` : `-${absDelta}`}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.7rem' }}>
                            Score Gap
                        </Typography>
                    </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                    <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: '10px', bgcolor: alpha('#8b5cf6', 0.06), border: `1px solid ${alpha('#8b5cf6', 0.15)}` }}>
                        <Typography sx={{ fontWeight: 900, fontSize: '1.6rem', color: '#8b5cf6', lineHeight: 1 }}>
                            {missingKeywords}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.7rem' }}>
                            Missing Keywords
                        </Typography>
                    </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                    <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: '10px', bgcolor: alpha('#f59e0b', 0.06), border: `1px solid ${alpha('#f59e0b', 0.15)}` }}>
                        <Typography sx={{ fontWeight: 900, fontSize: '1.6rem', color: '#f59e0b', lineHeight: 1 }}>
                            {missingSignals}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.7rem' }}>
                            Missing Signals
                        </Typography>
                    </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                    <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: '10px', bgcolor: alpha('#6366f1', 0.06), border: `1px solid ${alpha('#6366f1', 0.15)}` }}>
                        <Typography sx={{ fontWeight: 900, fontSize: '1.6rem', color: '#6366f1', lineHeight: 1 }}>
                            {overlapPct}%
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.7rem' }}>
                            Topic Overlap
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
        </Paper>
    );
}

// ── AI quota upgrade banner ───────────────────────────────────────────────────
function AiUpgradeBanner() {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
            p: 2, borderRadius: '10px',
            bgcolor: alpha('#f59e0b', isDark ? 0.08 : 0.05),
            border: `1px solid ${alpha('#f59e0b', 0.25)}`,
            borderLeft: '3px solid #f59e0b',
        }}>
            <LockIcon sx={{ fontSize: 18, color: '#f59e0b', flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.83rem' }}>
                    AI Insights require credits
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    AI-powered recommendations and post rewrites are hidden. Top up to unlock them instantly.
                </Typography>
            </Box>
            <Button
                size="small" variant="contained"
                href="/app/billing"
                sx={{
                    borderRadius: '8px', fontWeight: 700, fontSize: '0.76rem',
                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                    flexShrink: 0, textTransform: 'none',
                    '&:hover': { background: 'linear-gradient(135deg, #d97706, #b45309)' },
                }}
            >
                Buy Top-up Credits
            </Button>
        </Box>
    );
}

// ── Performance Radar card ────────────────────────────────────────────────────
function PerformanceRadarCard({ your, competitor, gaps, card }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const signalScore = (signals) =>
        Math.round(SIGNAL_DEFINITIONS.filter(s => signals[s.key]).length / SIGNAL_DEFINITIONS.length * 100);

    const yourSignals = signalScore(your.signals);
    const theirSignals = signalScore(competitor.signals);
    const overlapPct = Math.round((gaps.topic_overlap || 0) * 100);
    // Word count: normalize by capping at 400w → 100%
    const yourLength = Math.min(Math.round((your.word_count / 400) * 100), 100);
    const theirLength = Math.min(Math.round((competitor.word_count / 400) * 100), 100);

    const radarData = [
        { dim: 'Score',        yours: your.overall_score,      theirs: competitor.overall_score },
        { dim: 'Readability',  yours: your.readability * 10,   theirs: competitor.readability * 10 },
        { dim: 'Engagement',   yours: yourSignals,             theirs: theirSignals },
        { dim: 'Topics',       yours: overlapPct,              theirs: 100 },
        { dim: 'Length',       yours: yourLength,              theirs: theirLength },
    ];

    const dimLabels = {
        Score: 'Overall content quality score',
        Readability: 'Readability (10-pt scale × 10)',
        Engagement: 'Engagement hooks present',
        Topics: 'Topic coverage vs competitor',
        Length: 'Content length (400w = 100%)',
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        const yours = payload.find(p => p.dataKey === 'yours')?.value ?? 0;
        const theirs = payload.find(p => p.dataKey === 'theirs')?.value ?? 0;
        const delta = theirs - yours;
        return (
            <Box sx={{
                p: 1.5, borderRadius: '10px',
                bgcolor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                minWidth: 160,
            }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.primary', display: 'block', mb: 0.75 }}>
                    {label}
                </Typography>
                <Typography variant="caption" sx={{ color: '#6366f1', fontWeight: 700, display: 'block' }}>
                    Yours: {yours}
                </Typography>
                <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 700, display: 'block', mb: 0.5 }}>
                    Competitor: {theirs}
                </Typography>
                <Typography variant="caption" sx={{ color: delta > 0 ? '#ef4444' : '#10b981', fontWeight: 700 }}>
                    Gap: {delta > 0 ? `−${delta}` : `+${Math.abs(delta)}`}
                </Typography>
            </Box>
        );
    };

    return (
        <Paper elevation={0} sx={card}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <RadarIcon sx={{ color: '#6366f1', fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Performance Radar</Typography>
                <Chip
                    label="5 dimensions"
                    size="small"
                    sx={{ ml: 'auto', height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: alpha('#6366f1', 0.1), color: '#6366f1' }}
                />
            </Box>

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {/* Radar chart — left */}
                <Box sx={{ flex: '1 1 280px', minWidth: 240 }}>
                    <ResponsiveContainer width="100%" height={260}>
                        <RadarChart data={radarData} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                            <PolarGrid stroke={alpha(isDark ? '#fff' : '#000', 0.08)} />
                            <PolarAngleAxis
                                dataKey="dim"
                                tick={{ fontSize: 11, fontWeight: 600, fill: theme.palette.text.secondary }}
                            />
                            <Radar
                                name="Your Content"
                                dataKey="yours"
                                stroke="#6366f1"
                                fill={alpha('#6366f1', isDark ? 0.25 : 0.18)}
                                strokeWidth={2}
                            />
                            <Radar
                                name="Top Performer"
                                dataKey="theirs"
                                stroke="#10b981"
                                fill={alpha('#10b981', isDark ? 0.2 : 0.13)}
                                strokeWidth={2}
                                strokeDasharray="4 2"
                            />
                            <ChartTooltip content={<CustomTooltip />} />
                            <Legend
                                iconType="circle"
                                iconSize={8}
                                wrapperStyle={{ fontSize: '0.75rem', paddingTop: 4 }}
                            />
                        </RadarChart>
                    </ResponsiveContainer>
                </Box>

                {/* Delta breakdown — right */}
                <Box sx={{ flex: '1 1 200px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1.5 }}>
                    {radarData.map(d => {
                        const delta = d.theirs - d.yours;
                        const isWinning = delta <= 0;
                        const color = isWinning ? '#10b981' : '#ef4444';
                        return (
                            <Tooltip key={d.dim} title={dimLabels[d.dim]} placement="left">
                                <Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.76rem' }}>
                                            {d.dim}
                                        </Typography>
                                        <Box sx={{
                                            px: 0.75, py: 0.1, borderRadius: '5px',
                                            bgcolor: alpha(color, 0.12),
                                        }}>
                                            <Typography variant="caption" sx={{ fontWeight: 800, color, fontSize: '0.7rem' }}>
                                                {isWinning ? `+${Math.abs(delta)}` : `−${Math.abs(delta)}`}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {/* Stacked bar: your score vs their score */}
                                    <Box sx={{ position: 'relative', height: 7, borderRadius: 4, bgcolor: alpha('#000', 0.06) }}>
                                        {/* Competitor bar (background reference) */}
                                        <Box sx={{
                                            position: 'absolute', left: 0, top: 0, bottom: 0,
                                            width: `${d.theirs}%`, maxWidth: '100%',
                                            bgcolor: alpha('#10b981', 0.25), borderRadius: 4,
                                        }} />
                                        {/* Your bar */}
                                        <Box sx={{
                                            position: 'absolute', left: 0, top: 0, bottom: 0,
                                            width: `${d.yours}%`, maxWidth: '100%',
                                            background: isWinning
                                                ? 'linear-gradient(90deg, #6366f1, #10b981)'
                                                : 'linear-gradient(90deg, #6366f1, #8b5cf6)',
                                            borderRadius: 4,
                                            transition: 'width 0.8s cubic-bezier(.4,0,.2,1)',
                                        }} />
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
                                        <Typography variant="caption" sx={{ color: '#6366f1', fontSize: '0.65rem', fontWeight: 600 }}>
                                            You: {d.yours}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#10b981', fontSize: '0.65rem', fontWeight: 600 }}>
                                            Top: {d.theirs}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Tooltip>
                        );
                    })}
                </Box>
            </Box>
        </Paper>
    );
}

// ── Score comparison card ─────────────────────────────────────────────────────
function ScoreCard({ your, competitor, gaps, card }) {
    const delta = competitor.overall_score - your.overall_score;
    const isAhead = delta <= 0;

    return (
        <Paper elevation={0} sx={{ ...card, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <AnalyticsIcon sx={{ color: '#6366f1', fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Score Comparison</Typography>
            </Box>

            {/* Rings side-by-side */}
            <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', mb: 2.5, gap: 1 }}>
                <ScoreRing
                    value={your.overall_score}
                    label="Your Content"
                    sub={`${your.word_count}w · ${your.readability}/10`}
                    color="#6366f1"
                    size={96}
                />
                <Box sx={{
                    px: 2, py: 1.25, borderRadius: '10px', textAlign: 'center',
                    background: `linear-gradient(135deg, ${alpha(isAhead ? '#10b981' : '#ef4444', 0.08)}, ${alpha('#6366f1', 0.06)})`,
                    border: `1px solid ${alpha(isAhead ? '#10b981' : '#ef4444', 0.2)}`,
                }}>
                    <Typography sx={{ fontWeight: 900, fontSize: '1.75rem', lineHeight: 1, color: isAhead ? '#10b981' : '#ef4444' }}>
                        {isAhead ? `+${Math.abs(delta)}` : `-${Math.abs(delta)}`}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.68rem' }}>
                        {isAhead ? 'pts ahead' : 'pts behind'}
                    </Typography>
                </Box>
                <ScoreRing
                    value={competitor.overall_score}
                    label="Top Performer"
                    sub={`${competitor.word_count}w · ${competitor.readability}/10`}
                    color="#10b981"
                    size={96}
                />
            </Box>

            {/* Topic overlap */}
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.82rem' }}>
                        Topic Overlap
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: '#6366f1', fontSize: '0.82rem' }}>
                        {Math.round(gaps.topic_overlap * 100)}%
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={Math.round(gaps.topic_overlap * 100)}
                    sx={{
                        height: 7, borderRadius: 4,
                        bgcolor: alpha('#6366f1', 0.1),
                        '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #6366f1, #8b5cf6)', borderRadius: 4 },
                    }}
                />
                <Typography variant="caption" sx={{ color: 'text.disabled', mt: 0.5, display: 'block', fontSize: '0.72rem' }}>
                    {gaps.topic_overlap < 0.3
                        ? 'Low overlap — big topic gaps exist'
                        : gaps.topic_overlap < 0.6
                        ? 'Moderate overlap — some topics to cover'
                        : 'High overlap — content is well-aligned'}
                </Typography>
            </Box>
        </Paper>
    );
}

// ── Engagement signals card ───────────────────────────────────────────────────
function SignalsCard({ your, competitor, gaps, card }) {
    return (
        <Paper elevation={0} sx={{ ...card, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TrendingUpIcon sx={{ color: '#10b981', fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Engagement Signals</Typography>
                {gaps.missing_signals.length > 0 && (
                    <Chip
                        label={`${gaps.missing_signals.length} missing`}
                        size="small"
                        sx={{ ml: 'auto', bgcolor: alpha('#f59e0b', 0.1), color: '#d97706', fontWeight: 700, height: 22, fontSize: '0.7rem' }}
                    />
                )}
            </Box>

            {/* Column headers */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 120px 1fr', gap: 1, mb: 0.75 }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: '#6366f1', textAlign: 'right', fontSize: '0.68rem', letterSpacing: 0.5 }}>
                    YOURS
                </Typography>
                <Box />
                <Typography variant="caption" sx={{ fontWeight: 800, color: '#10b981', fontSize: '0.68rem', letterSpacing: 0.5 }}>
                    TOP PERFORMER
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {SIGNAL_DEFINITIONS.map(({ key, label }) => (
                    <SignalRow
                        key={key}
                        label={label}
                        yours={your.signals[key]}
                        theirs={competitor.signals[key]}
                    />
                ))}
            </Box>

            {gaps.missing_signals.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1.5, borderRadius: '8px', py: 0.5, fontSize: '0.78rem' }}>
                    Add {gaps.missing_signals.map(s => s.signal).join(', ')} to match top content
                </Alert>
            )}
        </Paper>
    );
}

// ── Missing keywords card (click to copy) ────────────────────────────────────
function KeywordsCard({ gaps, card }) {
    const [copied, setCopied] = useState(null);
    if (!gaps.missing_keywords.length) return null;

    const handleCopy = (word) => {
        navigator.clipboard?.writeText(word);
        setCopied(word);
        setTimeout(() => setCopied(null), 1500);
    };

    return (
        <Paper elevation={0} sx={{ ...card, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                <TagIcon sx={{ color: '#8b5cf6', fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Missing Keywords</Typography>
                <Box sx={{ ml: 'auto', px: 1.25, py: 0.25, borderRadius: '6px', bgcolor: alpha('#8b5cf6', 0.1), border: `1px solid ${alpha('#8b5cf6', 0.2)}` }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#7c3aed', fontSize: '0.68rem' }}>{gaps.missing_keywords.length} keywords</Typography>
                </Box>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5, fontSize: '0.8rem' }}>
                Found in top content but missing from yours. Click any keyword to copy it.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                {gaps.missing_keywords.map((kw, i) => (
                    <Tooltip key={i} title={copied === kw.word ? 'Copied!' : `Used ${kw.frequency}× — click to copy`}>
                        <Box
                            role="button"
                            tabIndex={0}
                            aria-label={`Copy keyword: ${kw.word}`}
                            onClick={() => handleCopy(kw.word)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCopy(kw.word); } }}
                            sx={{
                                px: 1.25, py: 0.4, borderRadius: '7px', cursor: 'pointer',
                                bgcolor: copied === kw.word ? alpha('#10b981', 0.12) : alpha('#8b5cf6', 0.07),
                                border: `1px solid ${alpha(copied === kw.word ? '#10b981' : '#8b5cf6', 0.25)}`,
                                borderLeft: `3px solid ${copied === kw.word ? '#10b981' : '#8b5cf6'}`,
                                display: 'flex', alignItems: 'center', gap: 0.75,
                                transition: 'all 0.15s',
                                '&:hover': { bgcolor: alpha('#8b5cf6', 0.15), transform: 'translateY(-1px)' },
                                opacity: Math.max(0.5, 1 - (i / gaps.missing_keywords.length) * 0.45),
                            }}
                        >
                            <Typography variant="caption" sx={{ fontWeight: 700, color: copied === kw.word ? '#10b981' : '#7c3aed', fontSize: '0.76rem' }}>
                                {kw.word}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                                ×{kw.frequency}
                            </Typography>
                        </Box>
                    </Tooltip>
                ))}
            </Box>
        </Paper>
    );
}

// ── Topic gaps card ───────────────────────────────────────────────────────────
function TopicsCard({ gaps, card }) {
    if (!gaps.missing_topics.length) return null;
    return (
        <Paper elevation={0} sx={{ ...card, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                <SearchIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Topic Gaps</Typography>
                <Box sx={{ ml: 'auto', px: 1.25, py: 0.25, borderRadius: '6px', bgcolor: alpha('#f59e0b', 0.1), border: `1px solid ${alpha('#f59e0b', 0.2)}` }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#d97706', fontSize: '0.68rem' }}>{gaps.missing_topics.length} topics</Typography>
                </Box>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5, fontSize: '0.8rem' }}>
                Themes covered by the competitor that your content doesn't address.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {gaps.missing_topics.map((t, i) => (
                    <Box key={i} sx={{
                        px: 1.25, py: 0.5, borderRadius: '7px',
                        bgcolor: alpha('#f59e0b', 0.08),
                        border: `1px solid ${alpha('#f59e0b', 0.25)}`,
                    }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#d97706', fontSize: '0.78rem' }}>{t}</Typography>
                    </Box>
                ))}
            </Box>
        </Paper>
    );
}

// ── Recommendations card ──────────────────────────────────────────────────────
function RecommendationsCard({ recommendations, card }) {
    if (!recommendations.length) return null;

    // Sort: high → medium → low
    const sorted = [...recommendations].sort((a, b) => {
        const order = { high: 0, medium: 1, low: 2 };
        return (order[a.priority] ?? 2) - (order[b.priority] ?? 2);
    });

    return (
        <Paper elevation={0} sx={card}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <AutoAwesomeIcon sx={{ color: '#ec4899', fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Actionable Recommendations</Typography>
                <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                    {['high', 'medium', 'low'].map(p => {
                        const count = sorted.filter(r => r.priority === p).length;
                        if (!count) return null;
                        return (
                            <Box key={p} sx={{ px: 1, py: 0.25, borderRadius: '6px', bgcolor: alpha(PRIORITY_COLOR[p], 0.1) }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: PRIORITY_COLOR[p], fontSize: '0.65rem' }}>
                                    {count} {p}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            <Grid container spacing={1.5}>
                {sorted.map((rec, i) => {
                    const color = PRIORITY_COLOR[rec.priority] || '#6366f1';
                    return (
                        <Grid size={{ xs: 12, md: 6 }} key={i}>
                            <Box sx={{
                                display: 'flex', gap: 1.5, p: 1.75, borderRadius: '10px', height: '100%',
                                background: PRIORITY_BG[rec.priority],
                                border: `1px solid ${alpha(color, 0.2)}`,
                                borderLeft: `3px solid ${color}`,
                                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                                '&:hover': { transform: 'translateY(-1px)', boxShadow: `0 4px 16px ${alpha(color, 0.14)}` },
                            }}>
                                <Avatar sx={{ bgcolor: alpha(color, 0.15), color, width: 30, height: 30, flexShrink: 0, fontSize: '0.78rem', fontWeight: 800 }}>
                                    {i + 1}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.4, flexWrap: 'wrap' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.83rem' }}>
                                            {rec.title}
                                        </Typography>
                                        <Box sx={{ px: 0.75, py: 0.1, borderRadius: '5px', bgcolor: alpha(color, 0.15) }}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color, fontSize: '0.62rem' }}>
                                                {rec.priority.toUpperCase()}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.55, fontSize: '0.78rem' }}>
                                        {rec.description}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    );
                })}
            </Grid>
        </Paper>
    );
}

// ── Suggested rewrites card ───────────────────────────────────────────────────
const VARIANT_COLORS = ['#6366f1', '#10b981', '#8b5cf6', '#f59e0b', '#ec4899'];

function SuggestionsCard({ suggestedPosts, card }) {
    const [copiedIdx, setCopiedIdx] = useState(null);
    const [snackOpen, setSnackOpen] = useState(false);

    if (!suggestedPosts?.length) return null;

    const handleCopy = (text, idx) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedIdx(idx);
            setSnackOpen(true);
            setTimeout(() => setCopiedIdx(null), 2000);
        });
    };

    return (
        <Paper elevation={0} sx={card}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <EditNoteIcon sx={{ color: '#6366f1', fontSize: 22 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Suggested Post Rewrites</Typography>
                <Box sx={{ ml: 'auto', px: 1.25, py: 0.25, borderRadius: '6px', bgcolor: alpha('#6366f1', 0.1), border: `1px solid ${alpha('#6366f1', 0.2)}` }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#6366f1', fontSize: '0.68rem' }}>{suggestedPosts.length} variants</Typography>
                </Box>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5, fontSize: '0.82rem' }}>
                Ready-to-use rewrites — each one closes a specific gap. Click the copy icon to grab any version.
            </Typography>

            <Grid container spacing={2}>
                {suggestedPosts.map((s, i) => {
                    const color = VARIANT_COLORS[i % VARIANT_COLORS.length];
                    const isCopied = copiedIdx === i;
                    return (
                        <Grid size={{ xs: 12, md: 6 }} key={i}>
                            <Box sx={{
                                p: 2.5, borderRadius: '10px', height: '100%',
                                border: `1px solid ${alpha(color, 0.22)}`,
                                background: `linear-gradient(135deg, ${alpha(color, 0.04)}, ${alpha(color, 0.02)})`,
                                display: 'flex', flexDirection: 'column', gap: 1.25,
                                transition: 'box-shadow 0.2s ease, transform 0.15s ease',
                                '&:hover': { boxShadow: `0 4px 20px ${alpha(color, 0.15)}`, transform: 'translateY(-1px)' },
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar sx={{ bgcolor: alpha(color, 0.14), color, width: 26, height: 26, fontSize: '0.7rem', fontWeight: 800, flexShrink: 0 }}>
                                        {i + 1}
                                    </Avatar>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', flex: 1, fontSize: '0.83rem' }}>
                                        {s.label}
                                    </Typography>
                                    <Box sx={{ px: 1, py: 0.2, borderRadius: '5px', bgcolor: alpha(color, 0.12), border: `1px solid ${alpha(color, 0.25)}` }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color, fontSize: '0.65rem' }}>{s.improvement}</Typography>
                                    </Box>
                                    <Tooltip title={isCopied ? 'Copied!' : 'Copy to clipboard'}>
                                        <IconButton size="small" onClick={() => handleCopy(s.text, i)}
                                            sx={{ color: isCopied ? '#10b981' : 'text.disabled', '&:hover': { color, bgcolor: alpha(color, 0.08) } }}>
                                            {isCopied ? <CopiedIcon sx={{ fontSize: 16 }} /> : <CopyIcon sx={{ fontSize: 16 }} />}
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.73rem' }}>
                                    {s.description}
                                </Typography>
                                <Box sx={{ p: 1.5, borderRadius: '8px', bgcolor: alpha(color, 0.04), border: `1px solid ${alpha(color, 0.12)}`, flex: 1 }}>
                                    <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.7, fontSize: '0.86rem', whiteSpace: 'pre-wrap' }}>
                                        {s.text}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    );
                })}
            </Grid>

            <Snackbar
                open={snackOpen}
                autoHideDuration={2000}
                onClose={() => setSnackOpen(false)}
                message="Copied to clipboard!"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Paper>
    );
}

// ── Personalization banner ────────────────────────────────────────────────────
function PersonalizationBanner({ personalized }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    if (!personalized?.has_profile) return null;
    const niche = personalized.niche;
    const platform = personalized.platform;
    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
            p: 1.75, borderRadius: '10px',
            bgcolor: alpha('#6366f1', isDark ? 0.1 : 0.06),
            border: `1px solid ${alpha('#6366f1', 0.2)}`,
            borderLeft: '3px solid #6366f1',
        }}>
            <NicheIcon sx={{ fontSize: 18, color: '#6366f1', flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#6366f1', fontSize: '0.83rem' }}>
                    Personalized analysis — based on your Content DNA Profile
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Scores and recommendations are tailored to your winning patterns
                    {niche ? ` in the "${niche}" niche` : ''}
                    {platform ? ` on ${platform}` : ''}.
                </Typography>
            </Box>
        </Box>
    );
}

// ── Profile alignment card ────────────────────────────────────────────────────
function ProfileAlignmentCard({ scores, personalized, card }) {
    const alignment = scores?.profile_alignment;
    if (alignment == null) return null;
    const color = alignmentColor(alignment);
    const label = alignmentLabel(alignment);
    const niche = personalized?.niche;
    const platform = personalized?.platform;

    return (
        <Paper elevation={0} sx={{ ...card, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AlignIcon sx={{ color: '#6366f1', fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Profile Alignment</Typography>
                <Chip label={label} size="small"
                    sx={{ ml: 'auto', height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: alpha(color, 0.12), color }} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
                <ScoreRing value={alignment} label="Content DNA Match" sub={niche ? `Niche: ${niche}` : ''} color={color} size={112} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[
                    { label: 'Word Count Range', desc: 'Matches your winning post length' },
                    { label: 'Engagement Signals', desc: 'Uses signals your audience responds to' },
                    { label: 'Brand Voice Tone', desc: 'Matches your detected tone' },
                    { label: 'Platform Fit', desc: platform ? `Optimised for ${platform}` : 'Platform-neutral' },
                    { label: 'CTA Pattern', desc: "Your audience's CTA expectation" },
                ].map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                            bgcolor: i < Math.round(alignment / 20) ? color : alpha(color, 0.3),
                        }} />
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.76rem' }}>
                                {item.label}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', display: 'block' }}>
                                {item.desc}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Paper>
    );
}

// ── AI Insights card ──────────────────────────────────────────────────────────
function AiInsightsCard({ aiInsights, card }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    if (!aiInsights) return null;

    const semanticGaps = aiInsights.semantic_gaps || [];
    const personalizedTips = aiInsights.personalized_tips || [];
    const platformTips = aiInsights.platform_specific_tips || [];
    const toneAnalysis = aiInsights.tone_analysis;
    const viralScore = aiInsights.viral_potential_score;
    const voiceRewrite = aiInsights.voice_aligned_rewrite;

    return (
        <Paper elevation={0} sx={card}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AutoAwesomeIcon sx={{ color: '#ec4899', fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>AI Insights</Typography>
                {viralScore?.your_score != null && (
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 0.75 }}>
                        <Chip label={`Your viral score: ${viralScore.your_score}`} size="small"
                            sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: alpha('#6366f1', 0.1), color: '#6366f1' }} />
                        {viralScore.competitor_score != null && (
                            <Chip label={`Competitor: ${viralScore.competitor_score}`} size="small"
                                sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: alpha('#ec4899', 0.1), color: '#ec4899' }} />
                        )}
                    </Box>
                )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {toneAnalysis && (
                    <Box sx={{
                        p: 1.5, borderRadius: '9px',
                        bgcolor: alpha('#ec4899', isDark ? 0.08 : 0.05),
                        border: `1px solid ${alpha('#ec4899', 0.15)}`,
                    }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#ec4899', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5, display: 'block' }}>
                            Tone Analysis
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.84rem' }}>
                            {toneAnalysis}
                        </Typography>
                    </Box>
                )}

                {semanticGaps.length > 0 && (
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.75, display: 'block' }}>
                            Semantic Gaps
                        </Typography>
                        {semanticGaps.slice(0, 5).map((gap, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                                <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#ec4899', mt: '7px', flexShrink: 0 }} />
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.84rem', lineHeight: 1.5 }}>{gap}</Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                {personalizedTips.length > 0 && (
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#10b981', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.75, display: 'block' }}>
                            Personalized Tips
                        </Typography>
                        {personalizedTips.map((tip, i) => (
                            <Box key={i} sx={{
                                display: 'flex', alignItems: 'flex-start', gap: 1, p: 1, mb: 0.5, borderRadius: '8px',
                                bgcolor: alpha('#10b981', isDark ? 0.07 : 0.04),
                                border: `1px solid ${alpha('#10b981', 0.15)}`,
                            }}>
                                <TipIcon sx={{ fontSize: 14, color: '#10b981', mt: '2px', flexShrink: 0 }} />
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.84rem', lineHeight: 1.5 }}>{tip}</Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                {platformTips.length > 0 && (
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#f59e0b', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.75, display: 'block' }}>
                            Platform Tips
                        </Typography>
                        {platformTips.map((tip, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                                <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#f59e0b', mt: '7px', flexShrink: 0 }} />
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.84rem', lineHeight: 1.5 }}>{tip}</Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                {voiceRewrite && (
                    <Box sx={{
                        p: 1.5, borderRadius: '9px',
                        bgcolor: alpha('#6366f1', isDark ? 0.08 : 0.05),
                        border: `1px solid ${alpha('#6366f1', 0.2)}`,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                            <VoiceIcon sx={{ fontSize: 14, color: '#6366f1' }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#6366f1', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                Voice-Aligned Rewrite
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.84rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                            {voiceRewrite}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Paper>
    );
}

// ── Orchestrator ──────────────────────────────────────────────────────────────
export default function GapsResults({ result, card, isPro = false }) {
    const { your, competitor, gaps, recommendations, suggested_posts } = result;
    const aiAvailable = result.ai_available !== false;
    const personalized = result.personalized || {};
    const aiInsights = result.ai_insights;
    const scores = result.scores;

    const hasKeywords = gaps.missing_keywords?.length > 0;
    const hasTopics = gaps.missing_topics?.length > 0;
    const hasAlignment = scores?.profile_alignment != null;
    const hasAiInsights = !!(aiInsights && (
        aiInsights.semantic_gaps?.length || aiInsights.personalized_tips?.length ||
        aiInsights.tone_analysis || aiInsights.platform_specific_tips?.length
    ));

    const [saveLoading, setSaveLoading] = useState(false);
    const [saveSnack, setSaveSnack] = useState('');

    const handleSave = async () => {
        setSaveLoading(true);
        try {
            await gapsApi.save(
                { your_score: scores?.your_overall, gap_score: scores?.gap_score, alignment: scores?.profile_alignment },
                your?.topics?.slice(0, 3).join(', '),
                personalized?.compare_mode,
                personalized?.platform,
            );
            setSaveSnack('Analysis saved!');
        } catch {
            setSaveSnack('Failed to save');
        } finally {
            setSaveLoading(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* 0. Personalization banner — shown for Pro users with profile */}
            <PersonalizationBanner personalized={personalized} />

            {/* 1. Gap Health Dashboard — full width */}
            <GapHealthBanner your={your} competitor={competitor} gaps={gaps} />

            {/* 2. AI quota banner — shown right after health banner when credits exhausted */}
            {!aiAvailable && <AiUpgradeBanner />}

            {/* 3. Performance Radar — full width visual */}
            <PerformanceRadarCard your={your} competitor={competitor} gaps={gaps} card={card} />

            {/* 4. Score + Signals + optional Profile Alignment — responsive row */}
            <Grid container spacing={2.5} alignItems="stretch">
                <Grid size={{ xs: 12, md: hasAlignment ? 4 : 5 }}>
                    <ScoreCard your={your} competitor={competitor} gaps={gaps} card={card} />
                </Grid>
                {hasAlignment && (
                    <Grid size={{ xs: 12, md: 4 }}>
                        <CardErrorBoundary>
                            <ProfileAlignmentCard scores={scores} personalized={personalized} card={card} />
                        </CardErrorBoundary>
                    </Grid>
                )}
                <Grid size={{ xs: 12, md: hasAlignment ? 4 : 7 }}>
                    <SignalsCard your={your} competitor={competitor} gaps={gaps} card={card} />
                </Grid>
            </Grid>

            {/* 5. AI Insights — full width when available */}
            {aiAvailable && hasAiInsights && (
                <CardErrorBoundary>
                    <AiInsightsCard aiInsights={aiInsights} card={card} />
                </CardErrorBoundary>
            )}

            {/* 6. Keywords + Topics — side by side when both exist */}
            {(hasKeywords || hasTopics) && (
                <CardErrorBoundary>
                    <Grid container spacing={2.5} alignItems="stretch">
                        {hasKeywords && (
                            <Grid size={{ xs: 12, sm: hasTopics ? 7 : 12 }}>
                                <KeywordsCard gaps={gaps} card={card} />
                            </Grid>
                        )}
                        {hasTopics && (
                            <Grid size={{ xs: 12, sm: hasKeywords ? 5 : 12 }}>
                                <TopicsCard gaps={gaps} card={card} />
                            </Grid>
                        )}
                    </Grid>
                </CardErrorBoundary>
            )}

            {/* 7. Recommendations — full width, 2-col grid internally */}
            {recommendations?.length > 0 && (
                <CardErrorBoundary>
                    <RecommendationsCard recommendations={recommendations} card={card} />
                </CardErrorBoundary>
            )}

            {/* 8. Suggested rewrites — only shown when AI available */}
            {aiAvailable && <SuggestionsCard suggestedPosts={suggested_posts} card={card} />}

            {/* 9. Save button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="outlined"
                    startIcon={saveLoading
                        ? <BookmarkIcon sx={{ fontSize: 18 }} />
                        : <BookmarkBorderIcon sx={{ fontSize: 18 }} />}
                    onClick={handleSave}
                    disabled={saveLoading}
                    sx={{ borderRadius: '9px', fontWeight: 700, fontSize: '0.82rem', textTransform: 'none' }}
                >
                    Save Analysis
                </Button>
            </Box>

            <Snackbar
                open={!!saveSnack}
                autoHideDuration={2500}
                onClose={() => setSaveSnack('')}
                message={saveSnack}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Box>
    );
}
